import * as Yup from 'yup';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { fCurrency, fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { createChequePayment } from 'src/redux/slices/cheque';

import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { ChequeStatus, IChequeItem } from 'src/types/cheque';

import DirectToSupplierCheckbox from './direct-to-supplier-checkbox';

interface IAddPaymentDialog {
  cheque: IChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  remainingAmount: number;
}

function AddPaymentDialog({
  cheque,
  isOpen,
  onClose,
  onSuccess,
  remainingAmount,
}: IAddPaymentDialog) {
  const dispatch = useAppDispatcher();
  const navigate = useNavigate();

  const addPaymentSchema = useMemo(
    () =>
      Yup.object().shape({
        paymentDate: Yup.date().required('Payment date is required'),
        paymentAmount: Yup.number()
          .required('Payment amount is required')
          .min(0.01, 'Payment amount must be at least 0.01')
          .max(
            remainingAmount / 100,
            `Payment cannot exceed remaining amount (${fCurrency(remainingAmount / 100)})`
          ),
        note: Yup.string().optional(),
        paidDirectToSupplier: Yup.boolean().default(false),
      }),
    [remainingAmount]
  );

  const defaultValues = useMemo(
    () => ({
      paymentDate: new Date(),
      paymentAmount: remainingAmount / 100,
      note: '',
      paidDirectToSupplier: false,
    }),
    [remainingAmount]
  );

  const methods = useForm({
    resolver: yupResolver(addPaymentSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const paymentResponse = await dispatch(
        createChequePayment({
          chequeId: cheque._id,
          payment: {
            paymentDate: new Date(data.paymentDate),
            amountInCents: data.paymentAmount * 100,
            note: data.note || undefined,
            paidDirectToSupplier: data.paidDirectToSupplier || undefined,
          },
        })
      ).unwrap();

      // Check if payment triggered auto-recovery (has recoveryHistory field)
      if (paymentResponse.recoveryHistory) {
        // Full payment - navigate to Recovered tab
        enqueueSnackbar('Payment recorded and cheque recovered successfully');
        navigate(paths.dashboard.cheque.root(ChequeStatus.RECOVERED));
      } else {
        // Partial payment - stay or refresh
        enqueueSnackbar('Payment recorded successfully');
      }

      reset();
      onSuccess();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message || 'Failed to add payment', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Add Payment</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Record a payment for this cheque. Remaining amount:{' '}
            <strong>{fCurrencyRupees(remainingAmount)}</strong>
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="paymentDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Payment Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            <RHFTextField
              name="paymentAmount"
              label="Payment Amount (Rupees)"
              type="number"
              required
              helperText={`Default is remaining amount: ${fCurrencyRupees(remainingAmount)}`}
            />

            <RHFTextField
              name="note"
              label="Payment Note (Optional)"
              multiline
              rows={3}
              placeholder="e.g., Bank transfer, cash payment, check number, etc."
            />

            {/* A held cheque never reached a supplier, so there is none to pay. */}
            {!cheque.heldAt && !cheque.bankedAt && (
              <DirectToSupplierCheckbox chequeId={cheque._id} amountField="paymentAmount" />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Add Payment
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default AddPaymentDialog;
